import React, { useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { ButtonStandard, Text } from '@stashinvest/react-components';
import { Illustration } from '@stashinvest/react-assets';
import { BanjoString } from '@stashinvest/react-banjo';
import Layout from 'stash/components/global/Layout';
import {
	SignUpWrapper,
	SignUpWrapperInner,
} from 'stash/components/sign-up/SignUpScreenLayout';
import { CONTACT_URL } from 'stash/constants';

type GraphicComponent = React.ComponentType;

export const HouseQuestionIllustration = () => (
	<Illustration
		illustrationToken="sds-assets/base/illustration/square/houseQuestion"
		size="XL"
	/>
);

export const MoneyQuestionIllustration = () => (
	<Illustration
		illustrationToken="sds-assets/base/illustration/square/debitCard"
		size="XL"
	/>
);

type BankErrorScreenProps = {
	graphic: GraphicComponent;
	header: React.ReactNode;
	subheader: React.ReactNode;
	cta: React.ReactNode;
	ctaLink?: string;
	ctaCallback?: () => void;
	secondaryCta?: React.ReactNode;
	secondaryCtaLink?: string;
	secondaryCtaCallback?: () => void;
	coreDisclosure?: React.ReactNode;
	handleMount?: () => void;
};

const useNavigateCta = () => {
	const history = useHistory();

	return (link?: string, callback?: () => void) => {
		callback?.();
		if (!link) {
			return;
		}
		if (link.startsWith('http://') || link.startsWith('https://')) {
			window.location.href = link;
			return;
		}
		history.push(link);
	};
};

export const BankErrorScreen = ({
	header,
	subheader,
	cta,
	ctaLink,
	ctaCallback,
	secondaryCta,
	secondaryCtaLink,
	secondaryCtaCallback,
	coreDisclosure,
	handleMount = () => {},
	graphic: Graphic,
}: BankErrorScreenProps) => {
	const navigateCta = useNavigateCta();

	useEffect(() => {
		handleMount();
		window.scrollTo(0, 0);
	}, [handleMount]);

	return (
		<Layout.PageWrap>
			<SignUpWrapper>
				<Graphic />
				<Text tag="h1" textStyle="headerSmall" className="mb-content2XS mt-contentSmall">
					{header}
				</Text>
				<SignUpWrapperInner>
					<Text tag="p" textStyle="bodyMediumStandard" className="mb-contentMedium">
						{subheader}
					</Text>
					<div className="flex flex-col items-center">
						<ButtonStandard
							fullWidth
							className="mb-content2XS"
							onClick={() => navigateCta(ctaLink, ctaCallback)}
						>
							{cta}
						</ButtonStandard>
						{secondaryCta && (
							<ButtonStandard
								fullWidth
								variant="tertiary"
								onClick={() => navigateCta(secondaryCtaLink, secondaryCtaCallback)}
							>
								{secondaryCta}
							</ButtonStandard>
						)}
					</div>
					{coreDisclosure && (
						<Text
							tag="p"
							textStyle="legalStandard"
							textAlign="center"
							color="text-onSurfaceVariant"
							className="mt-contentSmall"
						>
							{coreDisclosure}
						</Text>
					)}
				</SignUpWrapperInner>
			</SignUpWrapper>
		</Layout.PageWrap>
	);
};

type NoAccountScreenProps = {
	ctaCallback?: () => void;
	onMount?: () => void;
};

export const NoAccountScreen = ({ ctaCallback, onMount }: NoAccountScreenProps) => (
	<BankErrorScreen
		graphic={MoneyQuestionIllustration}
		header={"You're almost there—are you sure you want to walk away?"}
		subheader={'We haven’t finished creating a Stash banking account for you yet.'}
		cta={'I want to apply'}
		secondaryCta={"I don't want to apply"}
		ctaCallback={ctaCallback}
		secondaryCtaLink="/"
		handleMount={onMount}
	/>
);

type SkipFundingScreenProps = {
	onMount?: () => void;
	ctaCallback?: () => void;
	secondaryCtaCallback?: () => void;
};

export const SkipFundingScreen = ({
	onMount,
	ctaCallback,
	secondaryCtaCallback,
}: SkipFundingScreenProps) => (
	<BankErrorScreen
		graphic={MoneyQuestionIllustration}
		header={"Are you sure? We'll hold off on mailing your card until you're ready."}
		subheader={"You won't be able to use your new account without transferring money to it."}
		cta={'Continue to funding'}
		secondaryCta={"I don't want to use my new account"}
		secondaryCtaLink="/"
		ctaCallback={ctaCallback}
		secondaryCtaCallback={secondaryCtaCallback}
		handleMount={onMount}
	/>
);

type AddressErrorScreenProps = {
	secondaryCtaCallback?: () => void;
	ctaCallback?: () => void;
	onMount?: () => void;
};

export const AddressErrorScreen = ({
	secondaryCtaCallback,
	ctaCallback,
	onMount,
}: AddressErrorScreenProps) => (
	<BankErrorScreen
		graphic={HouseQuestionIllustration}
		header={'We’re having issues opening an account using the address you gave us.'}
		subheader={`This is usually related to a character count limit or special characters.
			Please contact us to continue.`}
		cta={'Contact us'}
		ctaCallback={ctaCallback}
		secondaryCta={'Back to my information'}
		ctaLink={CONTACT_URL}
		secondaryCtaCallback={secondaryCtaCallback}
		handleMount={onMount}
	/>
);

type NameErrorScreenProps = {
	secondaryCtaCallback?: () => void;
	ctaCallback?: () => void;
	onMount?: () => void;
};

export const NameErrorScreen = ({
	secondaryCtaCallback,
	ctaCallback,
	onMount,
}: NameErrorScreenProps) => (
	<BankErrorScreen
		graphic={HouseQuestionIllustration}
		header={'We’re having some issues opening your account.'}
		subheader={'Please contact us to continue.'}
		cta={'Contact us'}
		ctaCallback={ctaCallback}
		secondaryCta={'Back to my information'}
		ctaLink={CONTACT_URL}
		secondaryCtaCallback={secondaryCtaCallback}
		handleMount={onMount}
	/>
);

type RejectManualCureProps = {
	handleMount?: () => void;
	handleCtaClick?: () => void;
};

export const RejectManualCure = ({ handleMount, handleCtaClick }: RejectManualCureProps) => (
	<BankErrorScreen
		graphic={HouseQuestionIllustration}
		header={'Sorry, there may be an issue with your account.'}
		subheader={'Please reach out to our customer service team for help.'}
		cta={'Contact Support'}
		ctaLink={CONTACT_URL}
		ctaCallback={handleCtaClick}
		handleMount={handleMount}
		coreDisclosure={
			<>
				<BanjoString stringId="information/disclose/genericBankMention" />{' '}
				<BanjoString stringId="information/disclose/bankAndInvest" />
			</>
		}
	/>
);

type RejectNoCureProps = {
	handleMount?: () => void;
	handleContactClick?: () => void;
	handleCtaClick?: () => void;
};

export const RejectNoCure = ({
	handleMount,
	handleContactClick,
	handleCtaClick,
}: RejectNoCureProps) => (
	<BankErrorScreen
		graphic={HouseQuestionIllustration}
		header={'Sorry, we’re unable to open a debit account for you.'}
		subheader={
			<Text tag="p" textStyle="bodyMediumStandard">
				Please reach out to our customer service team for help through our{' '}
				<Text
					tag="a"
					href={CONTACT_URL}
					target="_blank"
					textStyle="bodyMediumLink"
					color="text-primary"
					onClick={handleContactClick}
				>
					contact form
				</Text>
				.
			</Text>
		}
		cta={'Got it'}
		ctaLink="/"
		coreDisclosure={
			<>
				<BanjoString stringId="information/disclose/genericBankMention" />{' '}
				<BanjoString stringId="information/disclose/bankAndInvest" />
			</>
		}
		ctaCallback={handleCtaClick}
		handleMount={handleMount}
	/>
);

type NoInvestAccountProps = {
	handleMount?: () => void;
	handleCtaClick?: () => void;
};

export const NoInvestAccount = ({ handleMount, handleCtaClick }: NoInvestAccountProps) => (
	<BankErrorScreen
		graphic={HouseQuestionIllustration}
		header={'Finish setting up your investment account.'}
		subheader={
			<Text tag="p" textStyle="bodyMediumStandard">
				You’ll need to finish setting up your personal investment account to use your debit
				account.
			</Text>
		}
		cta={'Continue'}
		coreDisclosure={
			<>
				<BanjoString stringId="information/disclose/genericBankMention" />{' '}
				<BanjoString stringId="information/disclose/bankAndInvest" />
			</>
		}
		ctaLink="/invest"
		ctaCallback={handleCtaClick}
		handleMount={handleMount}
	/>
);

type UnknownErrorProps = {
	handleMount?: () => void;
	handleContactClick?: () => void;
	handleCtaClick?: () => void;
};

export const UnknownError = ({
	handleMount,
	handleContactClick,
	handleCtaClick,
}: UnknownErrorProps) => (
	<BankErrorScreen
		graphic={HouseQuestionIllustration}
		header={'Sorry, there may be an issue with your debit account.'}
		cta={'Contact us'}
		ctaLink={CONTACT_URL}
		subheader={
			<Text tag="p" textStyle="bodyMediumStandard">
				Please reach out to our customer service team for help through our{' '}
				<Text
					tag="a"
					href={CONTACT_URL}
					target="_blank"
					textStyle="bodyMediumLink"
					color="text-primary"
					onClick={handleContactClick}
				>
					contact form
				</Text>
				.
			</Text>
		}
		ctaCallback={handleCtaClick}
		handleMount={handleMount}
		coreDisclosure={
			<>
				<BanjoString stringId="information/disclose/genericBankMention" />{' '}
				<BanjoString stringId="information/disclose/bankAndInvest" />
			</>
		}
	/>
);
